import React, { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Eye, EyeOff, Pencil, Plus, Trash2, Image } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import AppLayout from "../../components/AppLayout";
import MediaGalleryPicker from "../../components/MediaGalleryPicker";

const TYPE_OPTIONS = [
  ["image_banner", "Image banner"],
  ["html_embed", "HTML / embed"],
  ["affiliate_link", "Affiliate link"],
];
const PLACEMENT_OPTIONS = [
  ["landing", "Public landing"],
  ["free_members_main", "Free Members main"],
  ["below_every_video", "Below every video"],
  ["shop_affiliate", "Shop & Affiliate"],
  ["escort", "Escort"],
  ["companion", "Companion"],
  ["cast", "Cast & Crew"],
  ["above_footer", "Above footer"],
  ["below_footer", "Below footer"],
  ["sidebar", "Sidebar"],
  ["mobile_bottom_banner", "Mobile bottom banner"],
];

const emptyForm = () => ({
  title: "", description: "", type: "image_banner", destination_url: "",
  media_url: "", embed_code: "", button_label: "Shop Now", active: true, sponsored: false,
  order: 1, start_date: "", end_date: "", placement: "landing", styling_notes: "",
});

const toDateInput = (value) => value ? new Date(value).toISOString().slice(0, 16) : "";
const toPayload = (item) => ({
  title: item.title,
  description: item.description || "",
  type: item.type,
  destination_url: item.destination_url || "",
  media_url: item.media_url || "",
  embed_code: item.embed_code || "",
  button_label: item.button_label || "Learn more",
  active: item.active !== false,
  order: Number(item.order) || 0,
  start_date: item.start_date || null,
  end_date: item.end_date || null,
  placement: item.placement,
  styling_notes: item.styling_notes || "",
  sponsored: item.sponsored === true,
});

export default function AdsManagement() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [galleryPickerOpen, setGalleryPickerOpen] = useState(false);

  const loadItems = async () => {
    setLoading(true);
    try {
      const response = await api.get("/api/ads-affiliate/admin/items");
      setItems(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error("Could not load ads and affiliate placements");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadItems(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setFormOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ ...emptyForm(), ...toPayload(item), start_date: toDateInput(item.start_date), end_date: toDateInput(item.end_date) });
    setFormOpen(true);
  };

  const updateForm = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
  };

  const handleGallerySelect = (imageUrl) => {
    setForm((current) => ({ ...current, media_url: imageUrl }));
  };

  const saveItem = async (event) => {
    event.preventDefault();
    if (!form.title.trim()) return toast.error("Enter an internal title");
    if (!form.type || !form.placement) return toast.error("Choose an ad type and placement");
    if (form.type === "image_banner" && !form.media_url.trim()) return toast.error("Image banners need an image");
    if (form.type === "html_embed" && !form.embed_code.trim()) return toast.error("HTML / embed placements need embed code");
    if (form.type === "affiliate_link" && !form.destination_url.trim()) return toast.error("Affiliate links need a destination URL");
    if (form.destination_url.trim()) {
      try {
        const url = new URL(form.destination_url);
        if (!["http:", "https:"].includes(url.protocol)) throw new Error("scheme");
      } catch {
        return toast.error("Enter a valid destination URL");
      }
    }
    if (form.start_date && form.end_date && new Date(form.start_date) > new Date(form.end_date)) return toast.error("End date must be after start date");

    const payload = {
      ...toPayload(form),
      title: form.title.trim(),
      order: Number(form.order) || 0,
      start_date: form.start_date ? new Date(form.start_date).toISOString() : null,
      end_date: form.end_date ? new Date(form.end_date).toISOString() : null,
    };
    setSaving(true);
    try {
      if (editing) await api.put(`/api/ads-affiliate/admin/items/${editing.placement_id}`, payload);
      else await api.post("/api/ads-affiliate/admin/items", payload);
      toast.success(editing ? "Placement updated" : "Placement created");
      setFormOpen(false);
      setEditing(null);
      await loadItems();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not save placement");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (item) => {
    try {
      await api.put(`/api/ads-affiliate/admin/items/${item.placement_id}`, { ...toPayload(item), active: item.active === false });
      await loadItems();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not update placement");
    }
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Delete ${item.title}?`)) return;
    try {
      await api.delete(`/api/ads-affiliate/admin/items/${item.placement_id}`);
      toast.success("Placement deleted");
      await loadItems();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not delete placement");
    }
  };

  const moveItem = async (item, direction) => {
    const group = items.filter((entry) => entry.placement === item.placement).sort((a, b) => (a.order || 0) - (b.order || 0));
    const index = group.findIndex((entry) => entry.placement_id === item.placement_id);
    const target = index + direction;
    if (target < 0 || target >= group.length) return;
    [group[index], group[target]] = [group[target], group[index]];
    try {
      await Promise.all(group.map((entry, order) => api.put(`/api/ads-affiliate/admin/items/${entry.placement_id}`, { ...toPayload(entry), order })));
      await loadItems();
    } catch (error) {
      toast.error("Could not reorder placements");
    }
  };

  return (
    <AppLayout title="Ads & Affiliate Management" overline="PROMOTIONS · PARTNERS · MY TOY">
      <div className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6" data-testid="ads-affiliate-management">
        <header className="flex flex-wrap items-end justify-between gap-4 border-b border-white/10 pb-5">
          <div>
            <h1 className="text-2xl font-semibold text-white">Ads &amp; Affiliate Management</h1>
            <p className="mt-1 text-sm text-zinc-400">Manage scheduled promotions, partner links, and My Toy in one place.</p>
          </div>
          <button type="button" onClick={openCreate} className="inline-flex min-h-10 items-center gap-2 rounded-lg border border-purple-300/40 bg-purple-800/80 px-4 text-sm font-semibold text-white hover:bg-purple-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-300">
            <Plus size={17} aria-hidden="true" /> New placement
          </button>
        </header>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {PLACEMENT_OPTIONS.map(([value, label]) => (
            <div key={value} className="rounded-lg border border-white/10 bg-zinc-900/70 px-4 py-3">
              <p className="text-xs uppercase text-zinc-500">{label}</p>
              <p className="mt-1 text-xl font-semibold text-white">{items.filter((item) => item.placement === value).length}</p>
            </div>
          ))}
        </div>

        {formOpen && (
          <section className="rounded-lg border border-purple-300/20 bg-zinc-900/90 p-4 sm:p-6" aria-labelledby="placement-form-title">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 id="placement-form-title" className="text-lg font-semibold text-white">{editing ? "Edit placement" : "Create placement"}</h2>
              <button type="button" onClick={() => setFormOpen(false)} className="text-sm text-zinc-400 hover:text-white">Cancel</button>
            </div>
            <form onSubmit={saveItem} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="space-y-1 text-sm text-zinc-300">Internal title / label
                <input name="title" value={form.title} onChange={updateForm} required maxLength={180} className="w-full rounded-md border border-white/15 bg-black/40 px-3 py-2 text-white" />
              </label>
              <label className="space-y-1 text-sm text-zinc-300">Type
                <select name="type" value={form.type} onChange={updateForm} required className="w-full rounded-md border border-white/15 bg-zinc-900 px-3 py-2 text-white">
                  {TYPE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  {form.type === "my_toy" && <option value="my_toy">My Toy (existing)</option>}
                </select>
              </label>
              <label className="space-y-1 text-sm text-zinc-300">Placement
                <select name="placement" value={form.placement} onChange={updateForm} required className="w-full rounded-md border border-white/15 bg-zinc-900 px-3 py-2 text-white">
                  {PLACEMENT_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </label>
              <label className="space-y-1 text-sm text-zinc-300">Destination / affiliate URL {form.type === "affiliate_link" && <span className="text-rose-300">*</span>}
                <input name="destination_url" type="url" required={form.type === "affiliate_link"} value={form.destination_url} onChange={updateForm} placeholder="https://" className="w-full rounded-md border border-white/15 bg-black/40 px-3 py-2 text-white" />
              </label>
              {(form.type === "image_banner" || form.type === "affiliate_link") && <div className="space-y-1 text-sm text-zinc-300">
                <label>Image / media</label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setGalleryPickerOpen(true)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md border border-white/15 bg-black/40 hover:bg-black/60 text-white transition-colors"
                  >
                    <Image size={18} />
                    {form.media_url ? "Change Image" : "Select from Gallery"}
                  </button>
                  {form.media_url && (
                    <>
                      <div className="w-12 h-12 rounded-md border border-white/15 overflow-hidden bg-black/40"><img src={form.media_url} alt="Selected ad image" className="w-full h-full object-cover" /></div>
                      <button type="button" onClick={() => setForm((current) => ({ ...current, media_url: "" }))} className="text-xs text-zinc-400 underline">Remove</button>
                    </>
                  )}
                </div>
                {form.media_url && (
                  <p className="text-xs text-zinc-500 truncate">{form.media_url}</p>
                )}
              </div>}
              <label className="space-y-1 text-sm text-zinc-300">Button label
                <input name="button_label" value={form.button_label} onChange={updateForm} maxLength={80} className="w-full rounded-md border border-white/15 bg-black/40 px-3 py-2 text-white" />
              </label>
              {form.type === "html_embed" && <label className="space-y-1 text-sm text-zinc-300 sm:col-span-2">HTML / embed code
                <textarea name="embed_code" value={form.embed_code} onChange={updateForm} rows={5} required className="w-full rounded-md border border-white/15 bg-black/40 px-3 py-2 font-mono text-xs text-white" />
              </label>}
              <label className="space-y-1 text-sm text-zinc-300 sm:col-span-2">Description
                <textarea name="description" value={form.description} onChange={updateForm} rows={3} className="w-full rounded-md border border-white/15 bg-black/40 px-3 py-2 text-white" />
              </label>
              <label className="space-y-1 text-sm text-zinc-300">Display order
                <input name="order" type="number" value={form.order} onChange={updateForm} className="w-full rounded-md border border-white/15 bg-black/40 px-3 py-2 text-white" />
              </label>
              <label className="flex min-h-10 items-center gap-2 self-end text-sm text-zinc-200">
                <input name="active" type="checkbox" checked={form.active} onChange={updateForm} className="h-4 w-4 accent-purple-500" /> Active
              </label>
              {form.type === "affiliate_link" && <label className="flex min-h-10 items-center gap-2 self-end text-sm text-zinc-200"><input name="sponsored" type="checkbox" checked={form.sponsored} onChange={updateForm} className="h-4 w-4 accent-purple-500" /> Show Sponsored badge</label>}
              <label className="space-y-1 text-sm text-zinc-300">Start date (optional)
                <input name="start_date" type="datetime-local" value={form.start_date} onChange={updateForm} className="w-full rounded-md border border-white/15 bg-black/40 px-3 py-2 text-white" />
              </label>
              <label className="space-y-1 text-sm text-zinc-300">End date (optional)
                <input name="end_date" type="datetime-local" value={form.end_date} onChange={updateForm} className="w-full rounded-md border border-white/15 bg-black/40 px-3 py-2 text-white" />
              </label>
              <label className="space-y-1 text-sm text-zinc-300 sm:col-span-2">Placement / styling notes (optional)
                <textarea name="styling_notes" value={form.styling_notes} onChange={updateForm} rows={2} className="w-full rounded-md border border-white/15 bg-black/40 px-3 py-2 text-white" />
              </label>
              <div className="flex justify-end gap-2 sm:col-span-2">
                <button type="submit" disabled={saving} className="min-h-10 rounded-md bg-purple-700 px-5 text-sm font-semibold text-white hover:bg-purple-600 disabled:opacity-60">{saving ? "Saving…" : editing ? "Save changes" : "Create placement"}</button>
              </div>
            </form>
          </section>
        )}

        <section className="space-y-3" aria-label="Managed placements">
          {loading ? <p className="py-10 text-center text-sm text-zinc-400">Loading placements…</p> : items.length === 0 ? (
            <p className="rounded-lg border border-dashed border-white/15 py-10 text-center text-sm text-zinc-500">No placements yet.</p>
          ) : items.map((item) => (
            <article key={item.placement_id} className="flex min-w-0 flex-col gap-4 rounded-lg border border-white/10 bg-zinc-900/70 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="break-words font-semibold text-white">{item.title}</h2>
                  <span className="rounded border border-white/10 px-2 py-0.5 text-xs text-zinc-300">{TYPE_OPTIONS.find(([value]) => value === item.type)?.[1] || item.type}</span>
                  <span className="rounded border border-purple-300/20 px-2 py-0.5 text-xs text-purple-200">{PLACEMENT_OPTIONS.find(([value]) => value === item.placement)?.[1] || item.placement}</span>
                  <span className={`rounded px-2 py-0.5 text-xs ${item.active ? "bg-emerald-500/10 text-emerald-300" : "bg-zinc-700 text-zinc-300"}`}>{item.active ? "Active" : "Inactive"}</span>
                </div>
                {item.description && <p className="mt-1 break-words text-sm text-zinc-400">{item.description}</p>}
                <p className="mt-2 break-all text-xs text-zinc-500">Order {item.order || 0}{item.start_date ? ` · Starts ${new Date(item.start_date).toLocaleString()}` : ""}{item.end_date ? ` · Ends ${new Date(item.end_date).toLocaleString()}` : ""}</p>
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-1">
                <button type="button" onClick={() => moveItem(item, -1)} aria-label={`Move ${item.title} up`} title="Move up" className="rounded-md p-2 text-zinc-300 hover:bg-white/10"><ArrowUp size={16} /></button>
                <button type="button" onClick={() => moveItem(item, 1)} aria-label={`Move ${item.title} down`} title="Move down" className="rounded-md p-2 text-zinc-300 hover:bg-white/10"><ArrowDown size={16} /></button>
                <button type="button" onClick={() => toggleActive(item)} aria-label={`${item.active ? "Deactivate" : "Activate"} ${item.title}`} title={item.active ? "Deactivate" : "Activate"} className="rounded-md p-2 text-zinc-300 hover:bg-white/10">{item.active ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                <button type="button" onClick={() => openEdit(item)} aria-label={`Edit ${item.title}`} title="Edit" className="rounded-md p-2 text-zinc-300 hover:bg-white/10"><Pencil size={16} /></button>
                <button type="button" onClick={() => deleteItem(item)} aria-label={`Delete ${item.title}`} title="Delete" className="rounded-md p-2 text-red-300 hover:bg-red-500/10"><Trash2 size={16} /></button>
              </div>
            </article>
          ))}
        </section>
      </div>

      {/* Media Gallery Picker Modal */}
      <MediaGalleryPicker
        isOpen={galleryPickerOpen}
        onClose={() => setGalleryPickerOpen(false)}
        onSelect={handleGallerySelect}
        currentUrl={form.media_url}
      />
    </AppLayout>
  );
}